import React, { Suspense, lazy } from "react"
import { Routes, Route, Navigate, BrowserRouter, useNavigate } from "react-router-dom"
import { AppState, Auth0Provider } from "@auth0/auth0-react"
import { QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { Loader } from "./components/Loader"
import { PageLayout } from "./components/PageLayout/PageLayout"
import { EmptyPageLayout } from "./components/EmptyPageLayout/EmptyPageLayout"
import { ProtectedRoute } from "./components/ProtectedRoute/ProtectedRoute"
import AuthorizedApolloProvider from "./apollo/AuthorizedApolloProvider"
import { queryClient } from "./pages/futuro/QueryClient"
import { Maintenance } from "./pages/maintenance"
import { loadEnvVars } from "./utils/loadEnvVars"
import { User } from "./components/Menu/Menu"
import { useMaintenanceStatus } from "./hooks/useMaintenanceStatus"
import { ROUTES } from "./consts/routes"
import { SignUpSuccess } from "./pages/signup/signup-success"
import "./index.scss"
import { NotificationHub } from "./components/NotificationHub/NotificationHub"
import { ErrorBoundaryPublic } from "./components/ErrorBoundaryPublic"

const Futuro = lazy(() => import("./pages/futuro"))
const Application = lazy(() => import("./pages/application"))
const Dashboard = lazy(() => import("./pages/dashboard"))
const KYC = lazy(() => import("./pages/kyc"))
const ContractViewer = lazy(() => import("./pages/contracts-view"))
const Privacy = lazy(() => import("./pages/privacy"))
const Profile = lazy(() => import("./pages/profile"))
const Documents = lazy(() => import("./pages/documents"))
const SignUp = lazy(() => import("./pages/signup"))

const { auth0Domain, auth0ClientId, auth0Audience, auth0RedirectUri } = loadEnvVars()

const Auth0ProviderWithNavigate = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate()

  const onRedirectCallback = (appState?: AppState) => {
    const redirectTo = appState?.returnTo || window.location.pathname
    navigate(redirectTo, { replace: true })
  }

  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={auth0ClientId}
      authorizationParams={{
        audience: auth0Audience,
        redirect_uri: auth0RedirectUri
      }}
      useRefreshTokens
      useRefreshTokensFallback={true}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
    >
      {children}
    </Auth0Provider>
  )
}

const PrivateRoutes = () => (
  <Auth0ProviderWithNavigate>
    <NotificationHub>
      <AuthorizedApolloProvider>
        <Routes>
          {/* Do not change order */}
          <Route
            path="/kyc/*"
            element={
              <ProtectedRoute>
                <QueryClientProvider client={queryClient}>
                  <PageLayout>{(user: User) => <KYC user={user} />}</PageLayout>
                </QueryClientProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path="/contracts/:type?/:product?"
            element={
              <ProtectedRoute>
                <PageLayout>
                  {(_: User) => (
                    <QueryClientProvider client={queryClient}>
                      <ReactQueryDevtools />
                      <ContractViewer queryClient={queryClient} />
                    </QueryClientProvider>
                  )}
                </PageLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/futuro/*"
            element={
              <ProtectedRoute>
                <PageLayout>{(user: User) => <Futuro user={user} />}</PageLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/application"
            element={
              <ProtectedRoute>
                <QueryClientProvider client={queryClient}>
                  <EmptyPageLayout>{(user: User) => <Application user={user} />}</EmptyPageLayout>
                </QueryClientProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path="/privacy"
            element={
              <ProtectedRoute>
                <PageLayout>{(user: User) => <Privacy user={user} />}</PageLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.profile}
            element={
              <ProtectedRoute>
                <PageLayout>{(user: User) => <Profile user={user} />}</PageLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.dashboard}
            element={
              <ProtectedRoute>
                <QueryClientProvider client={queryClient}>
                  <PageLayout>{(user: User) => <Dashboard user={user} />}</PageLayout>
                </QueryClientProvider>
              </ProtectedRoute>
            }
          />

          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <QueryClientProvider client={queryClient}>
                  <PageLayout>{(user: User) => <Documents user={user} />}</PageLayout>
                </QueryClientProvider>
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to={ROUTES.dashboard} replace />} />
        </Routes>
      </AuthorizedApolloProvider>
    </NotificationHub>
  </Auth0ProviderWithNavigate>
)

export const App = () => {
  const { isActive, isLoading, error } = useMaintenanceStatus()

  if (isLoading) return <></>
  if (error) return <ErrorBoundaryPublic error={error} errorContainer />
  if (isActive) return <Maintenance />

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path={ROUTES.signup}
          element={
            <Suspense fallback={<Loader fullPage />}>
              <SignUp />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.signupSuccess}
          element={
            <Suspense fallback={<Loader fullPage />}>
              <SignUpSuccess />
            </Suspense>
          }
        />
        <Route path="/*" element={<PrivateRoutes />} />
      </Routes>
    </BrowserRouter>
  )
}
